// Percorso file: src/pages/ClinicCalendar.jsx
//
// IL CALENDARIO CLINIC DI UNA PERSONA — cantiere CLINICS / OFFSEASON,
// «CALENDARIO CLINIC», Fetta 2 (23/9/2026).
//
// La settimana di UNA persona — sé stessi o un figlio — con le sole sessioni
// clinic dei gruppi in cui è, negli eventi APERTI. Ci si arriva dal bottone
// «Calendar» delle righe ENROLLED di /clinics (ClinicMember.jsx).
//
// ⚠️ UNA PERSONA ALLA VOLTA, CON L'ID NELL'INDIRIZZO (/clinics/calendar/:personId):
// è il precedente di `/my-calendar/child/:childId` — il parametro, non lo stato
// di navigazione, che non sopravvive al ricaricamento.
//
// ⚠️ IL PERMESSO NON SI VALUTA QUI. Lo decide `GET /clinic-member/calendar/:personId`
// sul server: sé stessi, oppure un figlio di cui il chiamante è tutore
// registrato (`guardians` sul documento del figlio). Ogni altro caso è un 403
// unico, e la pagina lo rende con una frase neutra che non dice se quell'id
// esista.
//
// ⚠️ IL SERVER MANDA SOLO CIÒ CHE SERVE: per evento il titolo, il nome del
// gruppo della persona ("" se nessuno) e le sessioni di quel gruppo. Nessun
// nome di persona — perciò la riga di contesto sotto il titolo dice la
// SEZIONE («Clinic / Off Season / Specials»), non chi si sta guardando.
//
// ⚠️ LA VESTE È QUELLA DI MyCalendar.css (`qpsc-cal-*`, doc 07 §13), importata
// e NON modificata, come fa CoachAttendance.jsx. Il componente MyCalendar.jsx
// non si riusa: è una pagina legata a `GET /events`, al tipo Training/Game e
// alle squadre. Qui c'è una sola forma di card, allineata a sinistra, senza
// pastiglia di tipo, e nessun «Full Club Calendar».
//
// ⚠️ IL CONFINE: nessuna stagione, nessuna squadra, nessuna barra dell'annata.
//
// Testi a schermo in inglese (CLAUDE.md §3.7).

import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FaChevronLeft, FaChevronRight } from "react-icons/fa";
import HomeShell from "../components/HomeShell";
import { api } from "../api/api";
import { getWeekNumber, getWeekRange } from "../utils/calendario";
import "./MyCalendar.css";
import "./ClinicEvents.css";

// ===========================================================================
// AIUTANTI DI DATA — `getWeekRange` e `getWeekNumber` dalla sede unica
// utils/calendario.js (giro G-K1)
// ===========================================================================

/**
 * LA DATA DELLA SESSIONE come data LOCALE. `date` è una stringa AAAA-MM-GG
 * senza fuso (il server la valida così): si costruisce `new Date(y, m-1, d)`,
 * come `dataEvento` di utils/calendario.js, e MAI `new Date("AAAA-MM-GG")`, che la
 * legge come mezzanotte UTC — a ovest di Greenwich sarebbe il giorno prima.
 * Una data in altra forma non si colloca: `null`, e la sessione non si mostra.
 */
function dataSessione(sessione) {
  const testo = sessione && typeof sessione.date === "string" ? sessione.date : "";

  if (!/^\d{4}-\d{2}-\d{2}$/.test(testo)) return null;

  const [y, m, d] = testo.split("-").map(Number);
  return new Date(y, m - 1, d);
}

/** «THU 15/10» — la stessa resa della card di MyCalendar. */
function giornoLeggibile(data) {
  return `${data.toLocaleDateString("en-AU", { weekday: "short" }).toUpperCase()} ${data.toLocaleDateString("en-AU", { day: "2-digit", month: "2-digit" })}`;
}

/** «18:30» oppure «18:30–20:00». */
function oraLeggibile(sessione) {
  const inizio = typeof sessione.startTime === "string" ? sessione.startTime : "";
  const fine = typeof sessione.endTime === "string" ? sessione.endTime : "";

  if (!inizio) return "";
  return fine ? `${inizio}–${fine}` : inizio;
}

// In ordine di giorno e poi d'ora. AAAA-MM-GG e HH:MM a due cifre: il
// confronto fra stringhe È quello cronologico (stesso metro del server).
function ordinaSessioni(a, b) {
  return (
    a.date.localeCompare(b.date) ||
    a.startTime.localeCompare(b.startTime) ||
    a.title.localeCompare(b.title)
  );
}

const TESTO_SEZIONE = "Clinic / Off Season / Specials";

export default function ClinicCalendar() {
  const navigate = useNavigate();
  const { personId } = useParams();
  const idPersona = String(personId || "").trim();

  // "caricamento" | "ok" | "negato" | "errore"
  const [stato, setStato] = useState("caricamento");
  const [eventi, setEventi] = useState([]);

  useEffect(() => {
    let annullato = false;

    setStato("caricamento");
    setEventi([]);

    api
      .get(`/clinic-member/calendar/${encodeURIComponent(idPersona)}`)
      .then((dati) => {
        if (annullato) return;

        setEventi(dati && Array.isArray(dati.events) ? dati.events : []);
        setStato("ok");
      })
      .catch((err) => {
        if (annullato) return;

        setEventi([]);
        setStato(err && err.status === 403 ? "negato" : "errore");
      });

    return () => {
      annullato = true;
    };
  }, [idPersona]);

  // Tutte le sessioni di tutti gli eventi, in una fila sola, ciascuna col
  // titolo del suo evento e il nome del gruppo. Solo quelle con una data
  // leggibile: una data che non si colloca non si scrive finta.
  const sessioni = useMemo(() => {
    const fila = [];

    for (const evento of eventi) {
      if (!evento || !Array.isArray(evento.sessions)) continue;

      for (const s of evento.sessions) {
        if (!s || !dataSessione(s)) continue;

        fila.push({
          chiave: `${evento.eventId}|${s.id}`,
          date: s.date,
          startTime: typeof s.startTime === "string" ? s.startTime : "",
          endTime: typeof s.endTime === "string" ? s.endTime : "",
          notes: typeof s.notes === "string" ? s.notes : "",
          title: typeof evento.title === "string" ? evento.title : "",
          groupName: typeof evento.groupName === "string" ? evento.groupName : "",
        });
      }
    }

    return fila.sort(ordinaSessioni);
  }, [eventi]);

  // Gli eventi in cui la persona è iscritta SENZA gruppo: nessuna sessione, e
  // la pagina lo dice (una riga per evento, fuori dalle card).
  const senzaGruppo = eventi.filter(
    (e) => e && !(typeof e.groupName === "string" && e.groupName.trim())
  );

  // ---------------------------------------------------------------------
  // SETTIMANA — stesso navigatore di MyCalendar: si parte da OGGI, a
  // mezzanotte, e ci si sposta di sette giorni. Nessun salto automatico.
  // ---------------------------------------------------------------------
  const [currentWeek, setCurrentWeek] = useState(() => {
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    return now;
  });

  function prevWeek() {
    const d = new Date(currentWeek);
    d.setDate(d.getDate() - 7);
    setCurrentWeek(d);
  }
  function nextWeek() {
    const d = new Date(currentWeek);
    d.setDate(d.getDate() + 7);
    setCurrentWeek(d);
  }

  const [weekStartStr, weekEndStr, weekStart, weekEnd] = getWeekRange(currentWeek);
  const weekNum = getWeekNumber(currentWeek);

  const settimana = sessioni.filter((s) => {
    const d = dataSessione(s);
    return d >= weekStart && d <= weekEnd;
  });

  // «Next session» — sola informazione, sotto il vuoto. È la prima sessione
  // DOPO la settimana guardata e non prima di oggi: sulla settimana corrente è
  // semplicemente la prossima; guardando una settimana passata non si indica
  // come «prossima» una sessione già avvenuta.
  const oggi = new Date();
  oggi.setHours(0, 0, 0, 0);
  const prossima = settimana.length
    ? null
    : sessioni.find((s) => {
        const d = dataSessione(s);
        return d > weekEnd && d >= oggi;
      }) || null;

  return (
    <HomeShell backTo={() => navigate(-1)} showSwitch={false}>
      <h1 className="qpsc-page-title">Calendar</h1>

      <div className="qpsc-cal-context">
        <div className="qpsc-cal-context__row">
          <span className="qpsc-cal-context__team">{TESTO_SEZIONE}</span>
        </div>
      </div>

      {stato === "caricamento" && <p className="qpsc-cal-empty">Loading…</p>}

      {stato === "negato" && (
        <p className="qpsc-clinic__error" role="alert">
          This calendar is not available.
        </p>
      )}

      {stato === "errore" && (
        <p className="qpsc-clinic__error" role="alert">
          The calendar could not be loaded. Reload the page to try again.
        </p>
      )}

      {stato === "ok" && eventi.length === 0 && (
        <p className="qpsc-cal-empty">No clinic sessions to show.</p>
      )}

      {stato === "ok" && eventi.length > 0 && (
        <>
          <div className="qpsc-cal-weeknav">
            <button
              type="button"
              className="qpsc-cal-weeknav__arrow"
              onClick={prevWeek}
              aria-label="Previous week"
            >
              <FaChevronLeft />
            </button>

            <div className="qpsc-cal-weeknav__center">
              <span className="qpsc-cal-weeknav__dates">
                {weekStartStr} &mdash; {weekEndStr}
              </span>
              <span className="qpsc-cal-weeknav__week">Week {weekNum}</span>
            </div>

            <button
              type="button"
              className="qpsc-cal-weeknav__arrow"
              onClick={nextWeek}
              aria-label="Next week"
            >
              <FaChevronRight />
            </button>
          </div>

          {senzaGruppo.length > 0 && (
            <div className="qpsc-clinic__cal-notices">
              {senzaGruppo.map((e) => (
                <p key={e.eventId} className="qpsc-clinic__cal-notice">
                  {e.title}: not in a group yet — sessions will appear once the club
                  assigns one.
                </p>
              ))}
            </div>
          )}

          <div className="qpsc-cal-days">
            {settimana.length === 0 ? (
              <>
                <p className="qpsc-cal-empty">No clinic sessions this week.</p>
                {prossima && (
                  <p className="qpsc-clinic__cal-next">
                    Next session: {giornoLeggibile(dataSessione(prossima))}
                  </p>
                )}
              </>
            ) : (
              settimana.map((s) => (
                <article key={s.chiave} className="qpsc-cal-card qpsc-clinic__cal-card">
                  <span className="qpsc-cal-card__when">
                    {giornoLeggibile(dataSessione(s))}
                  </span>

                  {oraLeggibile(s) && (
                    <span className="qpsc-cal-card__time">{oraLeggibile(s)}</span>
                  )}

                  {s.title && <span className="qpsc-clinic__cal-title">{s.title}</span>}

                  {s.groupName && (
                    <span className="qpsc-clinic__cal-group">{s.groupName}</span>
                  )}

                  {s.notes.trim() && (
                    <span className="qpsc-cal-card__note">{s.notes}</span>
                  )}
                </article>
              ))
            )}
          </div>
        </>
      )}
    </HomeShell>
  );
}
